import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { Toast, ToastProps } from '@hopskipdrive/react-components';

import { ToastsContainer } from './styles';

interface ToastHookProps {
  children: ReactNode;
}
interface IToastContext {
  toasts: ToastProps[];
  addToast: (toast: ToastProps) => void;
  resetToasts: () => void;
}

const toastDurationMap = new Map([
  ['success', 3000],
  ['info', 3000],
  ['warning', 5000],
  ['error', 6000],
]);

const ToastContext = createContext<IToastContext | null>(null);

const ToastProvider: React.FC<ToastHookProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const addToast = useCallback(
    (newToast: ToastProps) => {
      setToasts((prevToasts) => [...prevToasts, newToast]);
    },
    [setToasts]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, resetToasts: () => setToasts([]) }}>
      {children}
      <ToastsContainer>
        {toasts.map((toast, index) => (
          <Toast
            key={index}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            autoHideDuration={toastDurationMap.get(toast.severity)}
            {...toast}
          />
        ))}
      </ToastsContainer>
    </ToastContext.Provider>
  );
};

function useToast(): IToastContext {
  const toastContext = useContext(ToastContext);

  if (!toastContext) {
    throw new Error('useToast must be used within an ToastProvider');
  }

  return toastContext;
}

export { ToastProvider, useToast };
